import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove
} from '@mui/icons-material';

const ProjectComparison = ({ data }) => {
  const [viewType, setViewType] = React.useState('bar');

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 300,
        flexDirection: 'column',
        color: 'text.secondary'
      }}>
        <Typography variant="h6" gutterBottom>
          No project data available
        </Typography>
        <Typography variant="body2">
          Comparison will show when multiple projects exist
        </Typography>
      </Box>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            {data.project_name}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Total Shipments:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.total_shipments.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Delivered:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.delivered.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Delivery Rate:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.delivery_rate}%
              </Typography>
            </Box>
            {data.avg_delivery_days && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Avg Days:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {data.avg_delivery_days}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const getPerformanceIcon = (rate) => {
    if (rate >= 80) return <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />;
    if (rate >= 60) return <Remove sx={{ color: 'warning.main', fontSize: 16 }} />;
    return <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />;
  };

  const getPerformanceColor = (rate) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis 
          dataKey="project_name" 
          stroke="#666"
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis stroke="#666" fontSize={12} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar dataKey="total_shipments" fill="#8884d8" name="Total Shipments" />
        <Bar dataKey="delivered" fill="#82ca9d" name="Delivered" />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderRadarChart = () => {
    // Normalize data for radar chart (scale to 0-100)
    const maxShipments = Math.max(...data.map(d => d.total_shipments));
    const maxDelivered = Math.max(...data.map(d => d.delivered));
    const maxAvgDays = Math.max(...data.map(d => d.avg_delivery_days || 0));

    const radarData = data.map(project => ({
      project: project.project_name.length > 10 ? project.project_name.substring(0, 10) + '...' : project.project_name,
      'Shipment Volume': (project.total_shipments / maxShipments) * 100,
      'Delivery Rate': project.delivery_rate,
      'Delivery Speed': maxAvgDays > 0 ? Math.max(0, 100 - ((project.avg_delivery_days || maxAvgDays) / maxAvgDays) * 100) : 50,
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart data={radarData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="project" tick={{ fontSize: 10 }} />
          <PolarRadiusAxis 
            angle={30} 
            domain={[0, 100]} 
            tick={{ fontSize: 8 }}
          />
          <Radar
            name="Shipment Volume"
            dataKey="Shipment Volume"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.3}
          />
          <Radar
            name="Delivery Rate"
            dataKey="Delivery Rate"
            stroke="#82ca9d"
            fill="#82ca9d"
            fillOpacity={0.3}
          />
          <Radar
            name="Delivery Speed"
            dataKey="Delivery Speed"
            stroke="#ffc658"
            fill="#ffc658"
            fillOpacity={0.3}
          />
          <Legend />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
    );
  };

  const renderTableView = () => (
    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Shipments</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Delivered</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Avg Days</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Performance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((project, index) => (
            <TableRow key={project.project_id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: index === 0 ? 'success.main' : index === 1 ? 'warning.main' : 'info.main'
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {project.project_name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {project.total_shipments.toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {project.delivered.toLocaleString()}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={project.delivery_rate}
                  sx={{
                    mt: 0.5,
                    height: 3,
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getPerformanceColor(project.delivery_rate) + '.main',
                      borderRadius: 1,
                    }
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={`${project.delivery_rate}%`}
                  color={getPerformanceColor(project.delivery_rate)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {project.avg_delivery_days ? `${project.avg_delivery_days}d` : 'N/A'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {getPerformanceIcon(project.delivery_rate)}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Project performance comparison
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Comparing {data.length} projects
          </Typography>
        </Box>
        
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="bar">
            Chart
          </ToggleButton>
          <ToggleButton value="radar">
            Radar
          </ToggleButton>
          <ToggleButton value="table">
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewType === 'bar' && renderBarChart()}
      {viewType === 'radar' && renderRadarChart()}
      {viewType === 'table' && renderTableView()}

      {/* Quick Stats */}
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: 'background.default', 
        borderRadius: 1,
        display: 'flex',
        justifyContent: 'space-around'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="primary">
            {data[0]?.project_name || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Best Performer
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            {data.reduce((sum, project) => sum + project.total_shipments, 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Volume
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="info.main">
            {(data.reduce((sum, project) => sum + project.delivery_rate, 0) / data.length).toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Avg Rate
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectComparison;